/* eslint-disable react/jsx-props-no-spreading */
import classNames from 'classnames';
import { FC, ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { SwipeEventData, useSwipeable } from 'react-swipeable';
import { IToast, ToastIconType } from '../../types/toasts';
import { Icons } from 'src/assets/fonts/Icons/icons';
import theme from 'src/constants/styles';
import { throttle } from 'src/utils/perfomance';
import Icon from 'src/view/components/Controls/Icon/Icon';
import { removeToast } from '~/utils/toasts';
import './style.scss';

interface IProps {
  data: IToast;
}

const Toast: FC<IProps> = ({ data }: IProps): JSX.Element => {
  const { id, content, display } = data;
  const [offset, setOffset] = useState<number>(0);

  useEffect(() => {
    if (!content.duration) return;
    const timer = setTimeout(() => removeToast(id), content.duration);
    return () => clearTimeout(timer);
  }, []);

  const requestClose = useCallback(
    throttle(() => {
      if (content.onRequestClose) content.onRequestClose();
      removeToast(id);
    }, 1000),
    []
  );

  const onSwiping = useCallback(
    throttle((options: SwipeEventData) => {
      setOffset(options.deltaX);
      if (options.absX > 50) requestClose();
    }, 30),
    []
  );
  const onSwiped = useCallback(() => {
    throttle((options: any) => {
      if (options.absX <= 100) setOffset(0);
    }, 1000);
  }, []);

  const handlers = useSwipeable({
    onSwiping,
    onSwiped,
    preventDefaultTouchmoveEvent: true,
    trackMouse: true,
  });

  const toastClassName = useMemo((): string => {
    const animateClass: string = display ? 'animate__fast animate__fadeInRight' : 'animate__faster animate__fadeOutRight';
    return classNames('toast animate__animated', content.contentClassName, animateClass);
  }, [display, content.contentClassName]);

  const icon = useMemo((): ReactElement | null => {
    switch (content.iconType) {
      case ToastIconType.ERROR:
        return <Icon name={Icons.Warning} size={20} color={theme.colors.red} />;
      case ToastIconType.WARNING:
        return <Icon name={Icons.Warning} size={20} color={theme.colors.yellow} />;
      case ToastIconType.SUCCESS:
        return <Icon name={Icons.Check} size={20} color={theme.colors.green} />;
      default:
        return null;
    }
  }, []);

  return (
    <div
      {...handlers}
      className={classNames('toast__wrapper', content.className)}
      style={{ transitionDuration: `100ms`, transform: `translateX(${offset}px)` }}
    >
      <div className={toastClassName}>
        {icon ? <div className="toast__icon-wrapper">{icon}</div> : null}

        <div className="toast__content">
          {content.title ? <span className="toast__title">{content.title}</span> : null}
          {content.message ? <span className="toast__message">{content.message}</span> : null}
          {content.Content ? <content.Content data={data} /> : null}
        </div>

        {content.hideCloseIcon ? null : (
          <span className="toast__close" onClick={requestClose}>
            <Icon name={Icons.Close} size={10} color={theme.colors.gray4} />
          </span>
        )}
      </div>
    </div>
  );
};

export default Toast;
